import React from 'react';

import type { HubItem, NewsletterSummary } from '../api/types';
import { NavLink } from 'react-router-dom';

import { LegendBlock, LegendKey } from '../components/Legend';
import { PlateWindow } from '../components/Plate';
import Spinner from '../components/Spinner';
import TerrainField from '../components/TerrainField';
import { useHubArchive } from '../hooks/useHubArchive';
import { useInfoHubMenu } from '../hooks/useInfoHubMenu';
import { useNewsletterArchive } from '../hooks/useNewsletterArchive';
import {
  buildArticleArchivePath,
  buildArticlePath,
  buildNewsArchivePath,
  buildNewsletterArchivePath,
  buildNewsletterPath,
  buildNewsPath,
  buildPagePath,
} from '../utils/contentPaths';

const NEWS_COUNT = 3;
// One lead plus three beside it — the shape of the composition, not a preference.
const BRIEF_COUNT = 4;
const BULLETIN_COUNT = 3;

const LABEL = 'font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted';

const formatDay = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d
        .toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
        .toUpperCase();
};

const formatMonth = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' }).toUpperCase();
};

/**
 * A section's opening line: the label, and the way through to everything behind it.
 *
 * The row is a fixed 1.5rem tall rather than baseline-aligned, so that two heads sitting
 * in adjacent columns line up. Baseline alignment does not survive the comparison: the
 * "All newsletters" link is a step larger than the label, so it sets a taller line box and
 * drags its own label 4px down relative to a head that has no link.
 */
const SectionHead: React.FC<{ label: string; to?: string; more?: string }> = ({
  label,
  to,
  more,
}) => (
  <div className="mb-6 flex h-6 items-center justify-between gap-6">
    <h2 className={LABEL}>{label}</h2>
    {to && more && (
      <NavLink
        to={to}
        className="shrink-0 font-ui text-step--1 text-ink-muted transition-colors duration-fast ease-sheet hover:text-ink"
      >
        {more}
      </NavLink>
    )}
  </div>
);

/* --- latest news ---------------------------------------------------------- */

/**
 * Three dated notices across one rule.
 *
 * This is a 3-up, and it is the sanctioned one. DESIGN.md's Three-Up Ban targets "a row
 * of equal same-size tiles carrying an icon, a heading and a paragraph"; the exception it
 * names is "plain hairline-topped text columns with no icon and no box", which is exactly
 * this — no icon, no box, no radius, structure carried by the rules between the columns.
 * It stays the minority pattern on the page: everything below it is a legend or a list.
 */
const LatestNews: React.FC<{ items: HubItem[] }> = ({ items }) => (
  <div className="grid grid-cols-1 border-y border-rule md:grid-cols-3">
    {items.map((item) => (
      <NavLink
        key={item.id}
        to={buildNewsPath(item.slug || item.id)}
        className="group border-b border-rule p-6 transition-colors duration-fast ease-sheet last:border-b-0 hover:bg-page-sunk md:border-b-0 md:border-r md:last:border-r-0"
      >
        {item.date && <div className={`mb-4 ${LABEL}`}>{formatDay(item.date)}</div>}
        <h3 className="mb-2 font-ui text-step-0 font-medium text-ink transition-colors duration-fast ease-sheet group-hover:text-apricot">
          {item.title}
        </h3>
        {item.summary && <p className="line-clamp-3 text-step-0 text-ink-muted">{item.summary}</p>}
      </NavLink>
    ))}
  </div>
);

/* --- the brief ------------------------------------------------------------ */

/**
 * The championed space: one piece given a plate and a column, three more beside it.
 *
 * WHAT THIS IS FED BY, AND WHAT IT SHOULD BE. It currently shows the newest articles,
 * which is honest but is not championing — nothing here is chosen. Real curation needs
 * `showOnLanding`, the team's own idea recorded as §9 item 5 of the handover: a bool on
 * the entity plus a toggle in the admin, so the landing is curated separately from the
 * menu. That is a Phase 1 change (entity → migration → mapping → DTO → client regen) and
 * the layout is built to take it without moving: swap the query, keep the composition,
 * and rename the label from "From the library" to whatever curation should call itself.
 *
 * Articles rather than content pages, deliberately. A page in the menu payload carries
 * only id, parent, path, title and position — no description and no picture at all — so
 * a championed page would arrive with an empty plate and no lede. An article already has
 * a summary, a category and a picture, which is what this composition is asking for.
 */
const Brief: React.FC<{ items: HubItem[] }> = ({ items }) => {
  const [lead, ...rest] = items;
  if (!lead) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-8">
      <div className="lg:col-span-7">
        <NavLink to={buildArticlePath(lead.slug || lead.id)} className="group block">
          <PlateWindow
            pictureUrl={lead.pictureUrl}
            reservedLabel={lead.categoryTitle}
            imageClassName="sepia-[.65] saturate-125 contrast-90 brightness-110 opacity-90 transition-[filter,opacity] duration-fast ease-sheet group-hover:sepia-0 group-hover:saturate-100 group-hover:contrast-100 group-hover:brightness-100 group-hover:opacity-100"
          />
          <h3 className="mt-6 font-ui text-step-1 text-ink transition-colors duration-fast ease-sheet group-hover:text-apricot">
            {lead.title}
          </h3>
          {lead.summary && (
            <p className="mt-2 max-w-measure text-step-0 text-ink-muted">{lead.summary}</p>
          )}
        </NavLink>
      </div>

      <div className="flex flex-col lg:col-span-5">
        {rest.map((item) => (
          <NavLink
            key={item.id}
            to={buildArticlePath(item.slug || item.id)}
            className="group -mx-4 border-t border-rule-strong px-4 py-6 transition-colors duration-fast ease-sheet last:border-b last:border-b-rule-strong hover:bg-page-sunk"
          >
            <h4 className="font-ui text-step-1 text-ink transition-colors duration-fast ease-sheet group-hover:text-apricot">
              {item.title}
            </h4>
            {item.summary && (
              <p className="mt-1 line-clamp-2 text-step-0 text-ink-muted">{item.summary}</p>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

/* --- bulletins ------------------------------------------------------------ */

const Bulletins: React.FC<{ items: NewsletterSummary[] }> = ({ items }) => (
  <div className="flex flex-col border-t border-rule">
    {items.map((n) => (
      <NavLink
        key={n.id}
        to={buildNewsletterPath(n.slug || n.id)}
        className="group -mx-4 flex items-center justify-between gap-4 border-b border-rule px-4 py-5 transition-colors duration-fast ease-sheet hover:bg-page-sunk"
      >
        <div className="min-w-0">
          {n.volume && <div className={`mb-1 ${LABEL}`}>Vol {n.volume}</div>}
          <div className="truncate font-ui text-step-0 font-medium text-ink transition-colors duration-fast ease-sheet group-hover:text-apricot">
            {n.title}
          </div>
        </div>
        <div className={`shrink-0 text-right ${LABEL}`}>
          {formatMonth(n.date) || (n.year !== null ? String(n.year) : '')}
        </div>
      </NavLink>
    ))}
  </div>
);

/* --- the landing ---------------------------------------------------------- */

/**
 * The info hub's landing.
 *
 * Four movements, in the order a member needs them: what just happened, what is worth
 * reading, the whole reference collection keyed by section, and the newsletter run. The
 * two legend blocks this replaced said the same things in one voice; the point of the
 * arrangement is that a dated notice and a standing reference no longer look alike.
 *
 * No apricot at rest on the sections below the hero. Stitch closed its landing with a
 * solid apricot "Browse Full Archive", and it is not adopted there: the accent marks
 * *the* primary action and a second one competing with it would make neither read as
 * the action. Every section below already carries its own way through, and apricot
 * only appears on hover, on the titles themselves — a state change, not a resting
 * accent. The hero is the one deliberate exception: the terrain field's pointer
 * highlight is apricot, same as the bar's own active-item underline, because that is
 * this surface's established use of the accent, not a second one competing with it.
 */
const InfoHubMain: React.FC = () => {
  const { sections, loading } = useInfoHubMenu();
  const { items: news } = useHubArchive('news', NEWS_COUNT);
  const { items: articles } = useHubArchive('article', BRIEF_COUNT);
  const { items: bulletins } = useNewsletterArchive(undefined, BULLETIN_COUNT);

  return (
    <div className="pb-12 sm:pb-16">
      {/* The hero: full-bleed terrain between the bar and Latest News, and nowhere
          else on the page. `-mx-4 lg:-mx-8` cancels HubContainer's own gutter so the
          field runs to the true viewport edges; the matching `px-4 lg:px-8` inside
          puts the heading back where it would have sat, so it still lines up with
          every section below it. */}
      <section className="relative -mx-4 overflow-hidden lg:-mx-8">
        <TerrainField className="absolute inset-0" />
        <div className="relative px-4 py-16 sm:py-20 lg:px-8">
          {/* One statement, in EB Garamond. No kicker above it — the No-Eyebrow Rule. */}
          <header className="max-w-measure">
            <h1 className="font-display text-step-4 leading-[1.02] tracking-[-0.02em] text-ink">
              Guides, announcements and resources, kept in one place.
            </h1>
          </header>
        </div>
      </section>

      {news.length > 0 && (
        <section className="mt-16 sm:mt-20">
          <SectionHead label="Latest news" to={buildNewsArchivePath()} more="All news" />
          <LatestNews items={news} />
        </section>
      )}

      {articles.length > 0 && (
        <section className="mt-16 sm:mt-20">
          <SectionHead
            label="From the library"
            to={buildArticleArchivePath()}
            more="All articles"
          />
          <Brief items={articles} />
        </section>
      )}

      <div className="mt-16 grid grid-cols-1 gap-12 sm:mt-20 lg:grid-cols-12 lg:gap-8">
        {/* The reference collection, keyed rather than listed. The sections are known
            before the fetch, so this block is the right shape immediately and only its
            tallies fill in. Rendering the tree's own top level here is what once made
            this page 17,549px tall: the legacy import surfaces 158 pages as roots. */}
        <section className="lg:col-span-7">
          <SectionHead label="Knowledge centre" />
          {loading ? (
            <div className="py-16">
              <Spinner size="lg" />
            </div>
          ) : (
            <LegendBlock head="Reference" columns={2}>
              {sections.map((section) => (
                <LegendKey
                  key={section.key}
                  to={buildPagePath(section.to)}
                  label={section.label}
                  count={section.pageCount}
                />
              ))}
            </LegendBlock>
          )}
        </section>

        {bulletins.length > 0 && (
          <section className="lg:col-span-5">
            <SectionHead
              label="Recent bulletins"
              to={buildNewsletterArchivePath()}
              more="All newsletters"
            />
            <Bulletins items={bulletins} />
          </section>
        )}
      </div>
    </div>
  );
};

export default InfoHubMain;
